import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface Invoice {
  id: string;
  project_id: string;
  project_name?: string;
  client_id: string;
  client_name?: string;
  number: string;
  date_issued: number;
  date_due: number;
  date_paid?: number;
  subtotal: number;
  tax: number;
  total: number;
  discount: number;
  status: 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'partial';
  notes?: string;
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface Payment {
  id: string;
  invoice_id: string;
  invoice_number?: string;
  amount: number;
  method: 'transfer' | 'cash' | 'card' | 'check';
  reference?: string;
  date_paid: number;
  notes?: string;
  created_by: string;
  created_at: number;
}

export interface FinancialSummary {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  pendingInvoices: number;
  overdueInvoices: number;
  paidInvoices: number;
  totalInvoices: number;
  monthlyRevenue: number;
  monthlyExpenses: number;
}

export const financeService = {
  async getInvoices(): Promise<Invoice[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/invoices`);
    return response.data || [];
  },

  async getInvoice(id: string): Promise<Invoice> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/invoices/${id}`);
    return response.data;
  },

  async createInvoice(data: Partial<Invoice>): Promise<Invoice> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/invoices`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async updateInvoice(id: string, data: Partial<Invoice>): Promise<Invoice> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async deleteInvoice(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/finance/invoices/${id}`, {
      method: 'DELETE',
    });
  },

  async updateInvoiceStatus(id: string, status: Invoice['status']): Promise<Invoice> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/finance/invoices/${id}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    );
    return response.data;
  },

  async getPayments(invoiceId?: string): Promise<Payment[]> {
    const url = invoiceId
      ? `${API_URL}/api/finance/payments?invoice_id=${invoiceId}`
      : `${API_URL}/api/finance/payments`;
    const response = await authService.fetchWithAuth(url);
    return response.data || [];
  },

  async createPayment(data: Partial<Payment>): Promise<Payment> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async getFinancialSummary(): Promise<FinancialSummary> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/finance/summary`);
    return response.data;
  },
};